"use client"

import { useState, useRef, useEffect } from "react"
import { ChevronRight, Search } from "lucide-react"

export default function IndustrySelector({ value, onChange, error, industryOptions }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [expandedCategory, setExpandedCategory] = useState(null)
  const [selectedIndustries, setSelectedIndustries] = useState([])
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Filter industries based on search term
  const filteredIndustries = Object.entries(industryOptions).filter(([category, subcategories]) => {
    if (!searchTerm) return true

    const categoryMatch = category.toLowerCase().includes(searchTerm.toLowerCase())
    const subcategoryMatch = subcategories.some((sub) => sub.toLowerCase().includes(searchTerm.toLowerCase()))

    return categoryMatch || subcategoryMatch
  })

  const handleCategoryClick = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category)
  }

  const handleSubcategoryChange = (subcategory, checked) => {
    let newSelected = [...selectedIndustries]

    if (checked) {
      newSelected.push(subcategory)
    } else {
      newSelected = newSelected.filter((item) => item !== subcategory)
    }

    setSelectedIndustries(newSelected)
    onChange(newSelected.join(", "))
  }

  const handleSelectAll = (category, checked) => {
    const subcategories = industryOptions[category]
    let newSelected = [...selectedIndustries]

    if (checked) {
      // Add all subcategories that aren't already selected
      subcategories.forEach((sub) => {
        if (!newSelected.includes(sub)) {
          newSelected.push(sub)
        }
      })
    } else {
      // Remove all subcategories from selection
      newSelected = newSelected.filter((item) => !subcategories.includes(item))
    }

    setSelectedIndustries(newSelected)
    onChange(newSelected.join(", "))
  }

  const getDisplayValue = () => {
    if (selectedIndustries.length === 0) return "Main Industry"
    if (selectedIndustries.length === 1) return selectedIndustries[0]
    return `${selectedIndustries.length} industries selected`
  }

  const isAllSelected = (category) => {
    const subcategories = industryOptions[category]
    return subcategories.every((sub) => selectedIndustries.includes(sub))
  }

  const isSomeSelected = (category) => {
    const subcategories = industryOptions[category]
    return subcategories.some((sub) => selectedIndustries.includes(sub))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full px-3 py-2 rounded-md cursor-pointer bg-white bg-opacity-10 flex items-center justify-between ${
          error && "border border-red-500"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedIndustries.length === 0 ? "text-gray-500" : "text-white"}>{getDisplayValue()}</span>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 bg-white border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="E.g. SaaS, Android, Cloud Computing"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full !pl-10 pr-3 py-2 !bg-white border !text-black placeholder:!text-black border-gray-300 rounded-md text-sm focus:outline-none "
              />
            </div>
          </div>

          <div className="flex max-h-[9rem]">
            {/* Left Panel - Categories */}
            <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
              {filteredIndustries.map(([category, subcategories]) => (
                <div key={category}>
                  <div
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between text-sm ${
                      expandedCategory === category ? "bg-blue-50 text-blue-700" : "text-gray-700"
                    } ${isSomeSelected(category) ? "font-medium" : ""}`}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <span>
                      {category} ({subcategories.length})
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${expandedCategory === category ? "rotate-90" : ""}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Right Panel - Subcategories */}
            <div className="w-1/2 overflow-y-auto">
              {expandedCategory && (
                <div className="p-3">
                  {/* Select All Option */}
                  <label className="!flex items-start space-x-2 mb-3 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={isAllSelected(expandedCategory)}
                      onChange={(e) => handleSelectAll(expandedCategory, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Select All ({industryOptions[expandedCategory].length})</span>
                  </label>

                  {/* Individual Subcategories */}
                  <div className="space-y-2">
                    {industryOptions[expandedCategory].map((subcategory) => (
                      <label key={subcategory} className="!flex items-center justify-start space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedIndustries.includes(subcategory)}
                          onChange={(e) => handleSubcategoryChange(subcategory, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-gray-700 w-[90%] truncate overflow-hidden whitespace-nowrap text-left">{subcategory}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {!expandedCategory && (
                <div className="p-3 text-sm text-gray-500 text-center">Select a category to view options</div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <span className="text-red-500 text-sm mt-1 block">{error}</span>}
    </div>
  )
}
